import {
  useDeferredValue,
  useId,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Icon, type IconName } from "./Icon";
import { EmptyState } from "./EmptyState";
import "./datatable.css";

/**
 * The standard table for any list in Echo: search, sort, and pagination
 * in one place.
 *
 * This exists because real data made flat lists unusable — a single Brand's
 * Ground Truth produced ~1,000 claims and ~270 prompts. A reviewer needs to
 * find "the ones about water" in one action, not scroll.
 *
 * Search and paging are CLIENT-side, which is the right trade-off at the page
 * sizes the API returns (≤200 rows, cursor-paginated per API_SPEC.md §1) and
 * keeps filtering instant. If a view ever needs to search the whole corpus
 * rather than the loaded page, that becomes a server-side query — this
 * component's contract doesn't change, only where `rows` comes from.
 */

export interface Column<T> {
  key: string;
  header: string;
  /** Cell content. */
  render: (row: T) => ReactNode;
  /** Plain text used for search and sort. Omit to exclude from both. */
  value?: (row: T) => string | number;
  align?: "left" | "right";
  width?: string;
}

interface DataTableProps<T> {
  rows: T[] | null;
  columns: Column<T>[];
  rowKey: (row: T) => string;
  /** Placeholder for the search box; also its accessible label. */
  searchPlaceholder?: string;
  pageSize?: number;
  empty: {
    icon: IconName;
    title: string;
    description: ReactNode;
    action?: ReactNode;
  };
  /** Rendered between the search box and the table — filters, bulk actions. */
  toolbar?: ReactNode;
  /** Optional per-row expansion, used for long text that shouldn't sit in a cell. */
  renderExpanded?: (row: T) => ReactNode;
}

export function DataTable<T>({
  rows,
  columns,
  rowKey,
  searchPlaceholder = "Search…",
  pageSize = 25,
  empty,
  toolbar,
  renderExpanded,
}: DataTableProps<T>) {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState<{ key: string; dir: "asc" | "desc" } | null>(
    null,
  );
  const [expanded, setExpanded] = useState<string | null>(null);
  const searchId = useId();

  // Keeps typing responsive when the filter runs over a few hundred rows.
  const deferredQuery = useDeferredValue(query);

  const searchable = useMemo(
    () => columns.filter((c) => c.value !== undefined),
    [columns],
  );

  const filtered = useMemo(() => {
    if (!rows) return null;
    const q = deferredQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) =>
      searchable.some((col) =>
        String(col.value!(row)).toLowerCase().includes(q),
      ),
    );
  }, [rows, deferredQuery, searchable]);

  const sorted = useMemo(() => {
    if (!filtered || !sort) return filtered;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.value) return filtered;
    return [...filtered].sort((a, b) => {
      const av = col.value!(a);
      const bv = col.value!(b);
      const cmp =
        typeof av === "number" && typeof bv === "number"
          ? av - bv
          : String(av).localeCompare(String(bv));
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [filtered, sort, columns]);

  if (rows === null) {
    return (
      <div className="datatable__loading">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="skeleton" style={{ height: 20 }} />
        ))}
        <span className="visually-hidden">Loading</span>
      </div>
    );
  }

  const total = sorted?.length ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = (sorted ?? []).slice(
    safePage * pageSize,
    safePage * pageSize + pageSize,
  );

  function toggleSort(key: string) {
    setPage(0);
    setSort((prev) =>
      prev?.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" },
    );
  }

  return (
    <div className="datatable">
      <div className="datatable__toolbar">
        <div
          className="datatable__search"
          // Searching an empty set is noise; the filters beside it are not.
          style={rows.length === 0 ? { visibility: "hidden" } : undefined}
        >
          <Icon name="search" size={16} />
          <input
            id={searchId}
            type="search"
            className="datatable__searchinput"
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(0);
            }}
          />
        </div>
        {toolbar}
        <span className="datatable__count" role="status">
          {total === rows.length
            ? `${total} ${total === 1 ? "row" : "rows"}`
            : `${total} of ${rows.length}`}
        </span>
      </div>

      {rows.length === 0 ? (
        // The empty state renders INSIDE the table body, never instead of the
        // whole component. Returning early here would unmount the toolbar —
        // and the toolbar is where the filters live, so an empty filter result
        // would remove the only control that could change the filter. That is
        // exactly the dead end this hit in review.
        <EmptyState {...empty} />
      ) : total === 0 ? (
        <div className="datatable__noresults">
          <p>
            Nothing matches <strong>“{query}”</strong>.
          </p>
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={() => setQuery("")}
          >
            Clear search
          </button>
        </div>
      ) : (
        <>
          <div className="datatable__scroll">
            <table className="table">
              <thead>
                <tr>
                  {renderExpanded && <th scope="col" style={{ width: 40 }} />}
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      scope="col"
                      className={col.align === "right" ? "numeric" : undefined}
                      style={col.width ? { width: col.width } : undefined}
                      aria-sort={
                        sort?.key === col.key
                          ? sort.dir === "asc"
                            ? "ascending"
                            : "descending"
                          : undefined
                      }
                    >
                      {col.value ? (
                        <button
                          type="button"
                          className="datatable__sortbtn"
                          onClick={() => toggleSort(col.key)}
                        >
                          {col.header}
                          <span
                            aria-hidden="true"
                            className="datatable__sortmark"
                          >
                            {sort?.key === col.key
                              ? sort.dir === "asc"
                                ? "▲"
                                : "▼"
                              : "↕"}
                          </span>
                        </button>
                      ) : (
                        col.header
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row) => {
                  const key = rowKey(row);
                  const isOpen = expanded === key;
                  return (
                    <>
                      <tr key={key}>
                        {renderExpanded && (
                          <td>
                            <button
                              type="button"
                              className="datatable__expand"
                              aria-expanded={isOpen}
                              aria-label={
                                isOpen ? "Collapse row" : "Expand row"
                              }
                              onClick={() => setExpanded(isOpen ? null : key)}
                            >
                              <Icon name="chevronDown" size={16} />
                            </button>
                          </td>
                        )}
                        {columns.map((col) => (
                          <td
                            key={col.key}
                            className={
                              col.align === "right" ? "numeric" : undefined
                            }
                          >
                            {col.render(row)}
                          </td>
                        ))}
                      </tr>
                      {isOpen && renderExpanded && (
                        <tr
                          key={`${key}-detail`}
                          className="datatable__detailrow"
                        >
                          <td colSpan={columns.length + 1}>
                            {renderExpanded(row)}
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>

          {pageCount > 1 && (
            <nav className="datatable__pager" aria-label="Pagination">
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                disabled={safePage === 0}
                onClick={() => setPage(safePage - 1)}
              >
                Previous
              </button>
              <span className="datatable__pageinfo">
                Page <strong className="tabular">{safePage + 1}</strong> of{" "}
                <strong className="tabular">{pageCount}</strong>
              </span>
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                disabled={safePage >= pageCount - 1}
                onClick={() => setPage(safePage + 1)}
              >
                Next
              </button>
            </nav>
          )}
        </>
      )}
    </div>
  );
}
